import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Send, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const AREAS = [
  { label: "Asesoría Fiscal",     value: "fiscal",   email: "fiscal@afpuertos.com" },
  { label: "Asesoría Laboral",    value: "laboral",  email: "laboral@afpuertos.com" },
  { label: "Asesoría Contable",   value: "contable", email: "fiscal@afpuertos.com" },
  { label: "Asesoría Jurídica",   value: "juridica", email: "info@afpuertos.com" },
];

export default function Contact() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    telefono: "",
    email: "",
    area: "",
    mensaje: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setRevealed(true);
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre || !formData.email) {
      toast.error("Por favor, completa al menos tu nombre y email.");
      return;
    }
    if (!formData.area) {
      toast.error("Por favor, selecciona el área de tu consulta.");
      return;
    }
    setSending(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || "";
      await axios.post(`${apiBase}/api/contact`, formData);
      toast.success("Mensaje enviado correctamente. Nos pondremos en contacto contigo pronto.");
      setFormData({ nombre: "", telefono: "", email: "", area: "", mensaje: "" });
    } catch (error) {
      toast.error("Error al enviar el mensaje. Por favor, inténtalo de nuevo o llámanos directamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contacto"
      ref={sectionRef}
      data-testid="contact-section"
      className="py-28 md:py-36 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className={`text-center mb-16 md:mb-20 transition-all duration-700 ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="text-[#c9a96e] text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Contacto
          </span>
          <h2
            data-testid="contact-title"
            className="text-3xl sm:text-4xl md:text-[3.25rem] font-light tracking-tight mt-5 text-[#1a1a1a] leading-[1.15]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Hablemos de tu proyecto
          </h2>
          <div className="w-12 h-[2px] bg-[#c9a96e] mx-auto mt-7" />
        </div>

        <div className="max-w-3xl mx-auto">
          <div className={`transition-all duration-700 ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="bg-[#f8f6f3] rounded-sm p-8 md:p-12 border border-[#e8e4de]">
              <p className="text-[13px] text-[#777777] font-light leading-[1.7] mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Rellena el formulario y nos pondremos en contacto contigo en menos de 24 horas. Sin compromiso.
              </p>
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#888888] font-semibold mb-2.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      Nombre *
                    </label>
                    <Input
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      placeholder="Tu nombre completo"
                      data-testid="input-nombre"
                      className="rounded-sm border-[#ddd8d0] bg-white h-12 text-sm font-light focus:border-[#c9a96e] focus-visible:ring-[#c9a96e]/20 placeholder:text-[#bbb]"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#888888] font-semibold mb-2.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      Teléfono
                    </label>
                    <Input
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      placeholder="Ej: 670 123 456"
                      data-testid="input-telefono"
                      className="rounded-sm border-[#ddd8d0] bg-white h-12 text-sm font-light focus:border-[#c9a96e] focus-visible:ring-[#c9a96e]/20 placeholder:text-[#bbb]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#888888] font-semibold mb-2.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Email *
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    data-testid="input-email"
                    className="rounded-sm border-[#ddd8d0] bg-white h-12 text-sm font-light focus:border-[#c9a96e] focus-visible:ring-[#c9a96e]/20 placeholder:text-[#bbb]"
                  />
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#888888] font-semibold mb-2.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Área de consulta *
                  </label>
                  <div className="relative">
                    <select
                      name="area"
                      value={formData.area}
                      onChange={handleChange}
                      data-testid="input-area"
                      className="w-full rounded-sm border border-[#ddd8d0] bg-white h-12 pl-3 pr-10 text-sm font-light text-[#1a1a1a] focus:outline-none focus:border-[#c9a96e] focus:ring-2 focus:ring-[#c9a96e]/20 appearance-none cursor-pointer"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      <option value="" disabled>Selecciona un área...</option>
                      {AREAS.map((area) => (
                        <option key={area.value} value={area.value}>
                          {area.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888] pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#888888] font-semibold mb-2.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Mensaje
                  </label>
                  <Textarea
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    placeholder="Cuéntanos cómo podemos ayudarte..."
                    rows={5}
                    data-testid="input-mensaje"
                    className="rounded-sm border-[#ddd8d0] bg-white text-sm font-light focus:border-[#c9a96e] focus-visible:ring-[#c9a96e]/20 placeholder:text-[#bbb] resize-none"
                  />
                </div>

                {/* Cláusula de protección de datos */}
                <p className="text-[11px] text-[#999] leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  En cumplimiento del RGPD, le informamos que los datos facilitados serán tratados por <strong>A.F. Puertos, S.L.</strong> con la finalidad de atender su consulta. Puede ejercer sus derechos en{" "}
                  <a href="mailto:info@afpuertos.com" className="text-[#c9a96e] hover:underline">info@afpuertos.com</a>.{" "}
                  Más información en nuestra{" "}
                  <Link to="/politica-de-privacidad" className="text-[#c9a96e] hover:underline">Política de Privacidad</Link>.
                </p>

                <div className="pt-2">
                  <Button
                    type="submit"
                    disabled={sending}
                    data-testid="submit-btn"
                    className="bg-[#c0392b] hover:bg-[#a93226] text-white rounded-sm uppercase tracking-[0.2em] text-[11px] font-semibold h-14 px-12 transition-all duration-300 shadow-lg shadow-[#c0392b]/15 w-full sm:w-auto"
                  >
                    {sending ? (
                      <span className="flex items-center gap-2.5">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Enviando...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2.5">
                        <Send className="w-4 h-4" />
                        Enviar mensaje
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
